import { requireCompletedProfile } from "@hisani/auth";
import { canUseStableFeature, canViewStableFeature, } from "../domain/membership/authorization-policy.js";
import { canAttachStopwatchRun } from "../domain/horses/horse-lifecycle.js";
import { attachStopwatchSessionSchema, listStopwatchSessionsSchema, stableForbidden, stableInvalidTransition, stableNotFound, } from "../validation.js";
/**
 * Attaches a completed Stopwatch session, timed within this Stable's own
 * context against one of its horses, to that horse's Stable training
 * record. Rides on the Stable's own Entity subscription rather than the
 * timer's personal User-level Pro (`COMPANY.md` "The boundary between the
 * two subscriptions"), so no subscription-tier check runs here beyond the
 * ordinary Sealed-feature authorization and horse-capability gate below —
 * Sealed features are not tier-gated (`docs/products/stable.md`:
 * "Paid tiers unlock the full Sealed feature suite ... not by feature
 * access"), only horse capacity is.
 *
 * Gated on TRAINING_SCHEDULER_LOGGING, the same permission that will govern
 * logging notes on a Training Scheduler entry once that UI exists — a
 * completed Stopwatch run is exactly that kind of log entry.
 */
export async function attachStopwatchSession(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = attachStopwatchSessionSchema.parse(input);
    const membership = await dependencies.repository.getMembershipContext(parsed.stableId, actor.account.id);
    if (membership === null)
        throw stableNotFound();
    if (!canUseStableFeature(membership, "TRAINING_SCHEDULER_LOGGING")) {
        throw stableForbidden();
    }
    const horse = await dependencies.repository.getHorse(parsed.stableId, parsed.horseId);
    if (horse === null)
        throw stableNotFound();
    if (!canAttachStopwatchRun(horse)) {
        throw stableInvalidTransition("This horse is not currently accepting new Stopwatch attachments.");
    }
    const now = dependencies.clock.now();
    return dependencies.repository.attachStopwatchSession({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        actorId: actor.account.id,
        label: parsed.label,
        elapsedMilliseconds: parsed.elapsedMilliseconds,
        sectionals: parsed.sectionals,
        occurredAt: parsed.occurredAt,
        now,
    });
}
export async function listStopwatchSessions(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listStopwatchSessionsSchema.parse(input);
    const membership = await dependencies.repository.getMembershipContext(parsed.stableId, actor.account.id);
    if (membership === null)
        throw stableNotFound();
    if (!canViewStableFeature(membership, "TRAINING_SCHEDULER_LOGGING")) {
        throw stableForbidden();
    }
    return dependencies.repository.listStopwatchSessions(parsed);
}
